/**
 * Site Group Settings V2 — "Admin" section: billing/manager options,
 * the feature list, and special instructions.
 *
 * Contains:
 * - AdminSection (default export)
 * - FormikUserSelect — Formik adapter for the manager-FK pickers
 */

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Box, List as MuiList, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import StatusChip from '@sophocles/components/StatusChip';
import UserAutoComplete from '@sophocles/components/UserAutoComplete';
import {
  createSiteGroupInstruction,
  deleteSiteGroupInstruction,
  fetchSiteGroupInstruction,
  fetchSiteGroupInstructionsForSiteGroup,
  patchSiteGroupInstruction,
} from '@sophocles/entities/SiteGroupInstruction';
import { fetchUsersForCustomerAndSiteGroup } from '@sophocles/entities/User';
import { loadFeatures, loadSiteGroupFeatureAssociations } from '@sophocles/features/featureFlags/api';
import FeatureCard from '@sophocles/features/featureFlags/components/FeatureCard';
import { selectSortedFeaturesForEntity } from '@sophocles/features/featureFlags/slice';
import {
  SettingsFieldRow,
  SettingsSection,
  SettingsSubsection,
  SettingsSwitchField,
  SpecialInstructionsList,
} from '@sophocles/views/settings/components';

// Formik-bound bridge to the modern MUI Autocomplete-based
// `UserAutoComplete` for the manager-FK pickers.  Stores the user
// id (matching the manager_id PATCH shape on the SiteGroupOptions
// endpoint) and resolves it back to the full user object out of
// the pre-fetched `users` list for display.
function FormikUserSelect({ name, label, users }) {
  const { values, setFieldValue } = useFormikContext();
  // Walk Formik values by dot-path so nested names like
  // "admin.options.ops_manager_id" resolve.
  const value = name.split('.').reduce((acc, key) => acc?.[key], values);
  return (
    <UserAutoComplete
      name={name}
      users={users || []}
      value={value ?? null}
      onChange={(_e, userId) => setFieldValue(name, userId ?? null)}
      label={label}
      size="small"
      fullWidth
      variant="filled"
      hiddenLabel
    />
  );
}

// Admin section.  Mirrors the legacy "Admin Options" tab 1:1 —
// three subsections bundled under a single tab, gated by
// `(is_staff || isReseller) && hasOptions`:
//
// 1. **Options** — billable / require_sites_bill_alternate flags
//    plus the five manager FKs.  Writes flow through the
//    consolidated PATCH (admin.options).
// 2. **Features** — FeatureCard tiles.  Toggles fire
//    createFeatureAssociation / editFeatureAssociation
//    immediately (separate endpoint), same as site-settings does
//    for site-level features.  Read-only on this PATCH.
// 3. **Instructions** — V2 SpecialInstructionsList component.
//    CRUD runs through the SiteGroupInstruction endpoints (via
//    the wired *Thunk callbacks below); separate from the
//    consolidated PATCH so the unified Save bar isn't responsible
//    for them.
//
// The auto-share rows the legacy OptionsForm sends
// (`auto_share_site_group_id`, `auto_share_third_party`) are NOT
// fielded here — the backend ignores them on the
// SiteGroupOptionsViewSet path (verified — no handler), so they
// were effectively dead in legacy.  Sharing logic lives in the
// dedicated Sharing section.
export default function AdminSection({ siteGroupId, customerId, isBillboard }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [users, setUsers] = React.useState([]);
  const [instructions, setInstructions] = React.useState([]);

  React.useEffect(() => {
    dispatch(loadFeatures());
    if (siteGroupId != null) {
      dispatch(loadSiteGroupFeatureAssociations(siteGroupId));
    }
  }, [dispatch, siteGroupId]);

  React.useEffect(() => {
    if (customerId == null || siteGroupId == null) return;
    dispatch(fetchUsersForCustomerAndSiteGroup(customerId, siteGroupId)).then(res => {
      if (Array.isArray(res)) setUsers(res);
    });
  }, [dispatch, customerId, siteGroupId]);

  const reloadInstructions = React.useCallback(() => {
    if (siteGroupId == null) return;
    dispatch(fetchSiteGroupInstructionsForSiteGroup(siteGroupId)).then(res => {
      if (res?.error) return;
      // res is an Immutable.List<Map> — keep it as-is; the
      // SpecialInstructionsList handles either shape via getField.
      setInstructions(res);
    });
  }, [dispatch, siteGroupId]);

  React.useEffect(() => {
    reloadInstructions();
  }, [reloadInstructions]);

  const siteGroupFeatures = useSelector(selectSortedFeaturesForEntity('sitegroup', siteGroupId));
  const isFeaturesEmpty = !siteGroupFeatures || siteGroupFeatures.length === 0;

  const groupTerm = t('siteGroups.siteGroup');
  const adminChip = <StatusChip color="info" label={t('siteSettings.adminOnly', 'Admin Only')} />;

  return (
    <SettingsSection
      id="settings-admin"
      icon={<AdminPanelSettingsIcon />}
      title={t('siteGroupSettings.v2.admin', 'Admin')}
      description={t(
        'siteGroupSettings.v2.adminDescription',
        '{{groupTerm}}-level options, available features, and special instructions.',
        { groupTerm: groupTerm },
      )}
      chip={adminChip}
    >
      <SettingsSubsection title={t('siteGroupSettings.v2.adminOptions', 'Options')} bordered>
        {!__EXCLUDE_BILLING__ && (
          <>
            <SettingsFieldRow
              label={t('siteGroupSettings.v2.billable', 'Billable')}
              description={t(
                'siteGroupSettings.v2.billableDescription',
                'When on, sites in this {{groupTerm}} count toward the customer invoice.',
                { groupTerm: groupTerm.toLowerCase() },
              )}
              controlWidth={80}
            >
              <SettingsSwitchField name="admin.options.billable" hideLabel />
            </SettingsFieldRow>
            <SettingsFieldRow
              label={t('siteGroupSettings.requireAlternateBilling', 'Require Alternate Billing')}
              description={t(
                'siteGroupSettings.v2.requireAlternateBillingDescription',
                'Force sites in this {{groupTerm}} to bill against a different {{groupTerm}}.',
                { groupTerm: groupTerm.toLowerCase() },
              )}
              controlWidth={80}
            >
              <SettingsSwitchField name="admin.options.require_sites_bill_alternate" hideLabel />
            </SettingsFieldRow>
          </>
        )}
      </SettingsSubsection>

      <SettingsSubsection title={t('siteGroupSettings.v2.adminManagers', 'Management')} bordered>
        <SettingsFieldRow
          label={t('siteGroupSettings.opsManager', 'Ops Manager')}
          description={t('siteGroupSettings.v2.opsManagerDescription', 'Operations point of contact.')}
          controlWidth={320}
        >
          <FormikUserSelect name="admin.options.ops_manager_id" label="" users={users} />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('siteGroupSettings.generalManager', 'General Manager')}
          description={t('siteGroupSettings.v2.generalManagerDescription', 'Senior point of contact.')}
          controlWidth={320}
        >
          <FormikUserSelect name="admin.options.general_manager_id" label="" users={users} />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('siteGroupSettings.regionalManager', 'Regional Manager')}
          description={t('siteGroupSettings.v2.regionalManagerDescription', 'Regional oversight.')}
          controlWidth={320}
        >
          <FormikUserSelect name="admin.options.regional_manager_id" label="" users={users} />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('siteGroupSettings.territoryManager', 'Territory Manager')}
          description={t('siteGroupSettings.v2.territoryManagerDescription', 'Territory oversight.')}
          controlWidth={320}
        >
          <FormikUserSelect name="admin.options.territory_manager_id" label="" users={users} />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('siteGroupSettings.areaFieldManager', 'Area Field Manager')}
          description={t('siteGroupSettings.v2.areaFieldManagerDescription', 'Field-level point of contact.')}
          controlWidth={320}
        >
          <FormikUserSelect name="admin.options.area_field_manager_id" label="" users={users} />
        </SettingsFieldRow>
      </SettingsSubsection>

      <SettingsSubsection
        title={t('siteGroupSettings.v2.features', 'Features')}
        description={t(
          'siteGroupSettings.v2.featuresDescription',
          'Toggleable {{groupTerm}}-level capabilities. Changes take effect immediately.',
          { groupTerm: groupTerm.toLowerCase() },
        )}
        bordered
        flush
      >
        {isFeaturesEmpty ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('siteGroupSettings.v2.noFeatures', 'No features available for this {{groupTerm}}.', {
                groupTerm: groupTerm.toLowerCase(),
              })}
            </Typography>
          </Box>
        ) : (
          <MuiList disablePadding sx={{ '& > li:last-of-type': { borderBottom: 0 } }}>
            {siteGroupFeatures.map(feature => (
              <FeatureCard key={feature.key} feature={feature} entityType="sitegroup" entityId={siteGroupId} />
            ))}
          </MuiList>
        )}
      </SettingsSubsection>

      <SettingsSubsection
        title={t('siteGroupSettings.v2.instructions', 'Special Instructions')}
        description={t(
          'siteGroupSettings.v2.instructionsDescription',
          'Operational notes for technicians visiting sites in this {{groupTerm}}.',
          { groupTerm: groupTerm.toLowerCase() },
        )}
        bordered
      >
        <Box sx={{ py: 1.5 }}>
          <SpecialInstructionsList
            instructions={instructions}
            onCreate={values => dispatch(createSiteGroupInstruction({ ...values, site_group: siteGroupId }))}
            onUpdate={(id, values) => dispatch(patchSiteGroupInstruction(id, values))}
            onDelete={id => dispatch(deleteSiteGroupInstruction(id))}
            onFetch={id => dispatch(fetchSiteGroupInstruction(id))}
            onReload={reloadInstructions}
          />
        </Box>
      </SettingsSubsection>
    </SettingsSection>
  );
}
